<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
        <el-input v-model="filters.couponNo" placeholder="券号" clearable class="filter-input" @keyup.enter="fetchList" />
        <el-input v-model="filters.ownerUserId" placeholder="推荐人用户ID" clearable class="filter-input" @keyup.enter="fetchList" />
        <el-input v-model="filters.ownerUsername" placeholder="或用户名" clearable class="filter-input" @keyup.enter="fetchList" />
        <el-select v-model="filters.couponType" placeholder="类型" clearable class="filter-select" @change="fetchList">
          <el-option label="双享礼券" value="normal" />
          <el-option label="臻享礼券" value="partner" />
          <el-option label="专享礼券" value="cash" />
        </el-select>
        <el-select v-model="filters.status" placeholder="状态" clearable class="filter-select" @change="fetchList">
          <el-option label="可用" value="active" />
          <el-option label="已使用" value="used" />
        </el-select>
        <el-button type="primary" @click="openCreateDialog">创建专享礼券</el-button>
        <el-button @click="fetchList">查询</el-button>
      </div>
    </div>

    <el-table :data="coupons" v-loading="loading" style="width: 100%">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column prop="couponNo" label="券号" width="130" />
      <el-table-column prop="displayName" label="名称" min-width="120" />
      <el-table-column label="推荐人" min-width="140">
        <template #default="scope">
          {{ scope.row.ownerNickname || scope.row.ownerUsername }}
          <div class="muted">ID: {{ scope.row.ownerUserId }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="couponType" label="类型" width="120">
        <template #default="scope">{{ couponTypeLabel(scope.row.couponType) }}</template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="80" />
      <el-table-column label="绑定用户" width="100">
        <template #default="scope">{{ scope.row.usedByUserId || '—' }}</template>
      </el-table-column>
      <el-table-column label="操作" width="90" fixed="right">
        <template #default="scope">
          <el-button
            v-if="scope.row.status === 'active'"
            link
            type="danger"
            @click="disableCoupon(scope.row)"
          >
            禁用
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <AdminPagination
      v-model:current-page="currentPage"
      v-model:page-size="pageSize"
      :total="total"
      @size-change="fetchList"
      @current-change="fetchList"
    />

    <el-dialog v-model="createDialogVisible" title="创建专享礼券" width="480px" destroy-on-close>
      <el-alert type="info" :closable="false" show-icon title="专享礼券由管理员创建，自定义券号，创建后不可修改；每张仅可使用一次。" style="margin-bottom: 16px" />
      <el-form label-width="96px">
        <el-form-item label="券主用户 ID" required>
          <el-input-number v-model="createForm.ownerUserId" :min="1" style="width: 100%" />
        </el-form-item>
        <el-form-item label="券号" required>
          <el-input v-model="createForm.couponNo" maxlength="20" placeholder="4～20 位，字母开头，不能以 RC 开头" />
        </el-form-item>
        <el-form-item label="名称" required>
          <el-input v-model="createForm.displayName" maxlength="20" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="submitCreate">创建</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import AdminPagination from '@/components/AdminPagination.vue'
import { couponTypeLabel } from '@/utils/referralCoupon'

export default {
  name: 'ReferralCouponManagement',
  components: { AdminPagination },
  data() {
    return {
      loading: false,
      creating: false,
      coupons: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      filters: { couponNo: '', ownerUserId: '', ownerUsername: '', couponType: '', status: '' },
      createDialogVisible: false,
      createForm: { ownerUserId: null, couponNo: '', displayName: '' }
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    couponTypeLabel,
    openCreateDialog() {
      this.createForm = { ownerUserId: null, couponNo: '', displayName: '' }
      this.createDialogVisible = true
    },
    async fetchList() {
      this.loading = true
      try {
        const ownerUserId = this.filters.ownerUserId ? Number(this.filters.ownerUserId) : 0
        const res = await axios.post('/api/v1/admin/referral/coupon/list', {
          page: this.currentPage,
          pageSize: this.pageSize,
          couponNo: this.filters.couponNo || undefined,
          ownerUserId,
          ownerUsername: this.filters.ownerUsername?.trim() || undefined,
          couponType: this.filters.couponType || undefined,
          status: this.filters.status || undefined
        })
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '加载失败')
          return
        }
        this.coupons = res.data.data?.coupons || []
        this.total = res.data.data?.total || 0
      } catch (e) {
        this.$message.error(e.response?.data?.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async disableCoupon(row) {
      try {
        await this.$confirm(`确定禁用礼券 ${row.couponNo}？禁用后不可再被绑定。`, '禁用礼券', { type: 'warning' })
      } catch {
        return
      }
      const res = await axios.post('/api/v1/admin/referral/coupon/disable', { couponId: row.id })
      if (res.data.code === 200) {
        this.$message.success('已禁用')
        await this.fetchList()
      } else {
        this.$message.error(res.data.message || '操作失败')
      }
    },
    async submitCreate() {
      if (!this.createForm.ownerUserId) {
        this.$message.warning('请填写券主用户 ID')
        return
      }
      if (!(this.createForm.couponNo || '').trim()) {
        this.$message.warning('请填写券号')
        return
      }
      if (!(this.createForm.displayName || '').trim()) {
        this.$message.warning('请填写名称')
        return
      }
      this.creating = true
      try {
        const res = await axios.post('/api/v1/admin/referral/coupon/create', {
          ownerUserId: this.createForm.ownerUserId,
          couponNo: this.createForm.couponNo.trim(),
          displayName: this.createForm.displayName.trim()
        })
        if (res.data.code !== 200) {
          this.$message.error(res.data.message || '创建失败')
          return
        }
        this.$message.success('专享礼券已创建')
        this.createDialogVisible = false
        await this.fetchList()
      } catch (e) {
        this.$message.error(e.response?.data?.message || '创建失败')
      } finally {
        this.creating = false
      }
    }
  }
}
</script>

<style scoped>
.muted { font-size: 12px; color: #909399; }
</style>
